import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { groupApi } from '../services/taskApi';
import { ItemGroupCreate, ItemGroupUpdate } from '../types';
import { BOARDS_QUERY_KEY } from './useBoards';

export const GROUPS_QUERY_KEY = 'groups';

export function useGroups(boardId: string, options?: { includeItems?: boolean }) {
  return useQuery({
    queryKey: [GROUPS_QUERY_KEY, boardId, options],
    queryFn: () => groupApi.listByBoard(boardId, options),
    enabled: !!boardId,
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boardId, data }: { boardId: string; data: ItemGroupCreate }) =>
      groupApi.create(boardId, data),
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY, boardId] });
      queryClient.invalidateQueries({ queryKey: [BOARDS_QUERY_KEY, boardId] });
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: ItemGroupUpdate }) =>
      groupApi.update(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BOARDS_QUERY_KEY] });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => groupApi.delete(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BOARDS_QUERY_KEY] });
    },
  });
}

export function useReorderGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boardId, groupIds }: { boardId: string; groupIds: string[] }) =>
      groupApi.reorder(boardId, groupIds),
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY, boardId] });
    },
  });
}
